import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { useGetMeiaMissionThreads } from '@queries/meia.query';
import { type FC, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MeiaTaskCanvas } from './meia-task-canvas.component';
import { MeiaThreadTabsTile } from './meia-thread-tabs-tile.component';

type EmptyThreadAction = {
  message: string;
  onClick: () => void;
};

type MeiaMissionTaskAuthoringProps = {
  canEdit: boolean;
  emptyThreadAction?: EmptyThreadAction;
  missionId: string;
  studyId: string;
};

export const MeiaMissionTaskAuthoring: FC<MeiaMissionTaskAuthoringProps> = (props) => {
  const { canEdit, emptyThreadAction, missionId, studyId } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedThreadId = searchParams.get('threadId');

  const { data: missionThreads = [], isLoading: isThreadsLoading } = useGetMeiaMissionThreads({
    missionId,
    studyId,
  });

  const validThread = missionThreads.find((thread) => thread.id === requestedThreadId) ?? missionThreads[0] ?? null;
  const validThreadId = validThread?.id ?? null;
  const editableEmptyThreadAction = canEdit ? emptyThreadAction : undefined;

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (validThreadId && next.get('threadId') !== validThreadId) {
      next.set('threadId', validThreadId);
      changed = true;
    }
    if (!validThreadId && next.has('threadId')) {
      next.delete('threadId');
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { preventScrollReset: true, replace: true });
    }
  }, [searchParams, setSearchParams, validThreadId]);

  const handleSelectThread = (threadId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('threadId', threadId);
    setSearchParams(next, { preventScrollReset: true, replace: false });
  };

  if (!isThreadsLoading && missionThreads.length === 0) {
    return (
      <EmptyStateContainer
        className="min-h-[520px] rounded-xl bg-white shadow-sm"
        message={
          editableEmptyThreadAction?.message ?? (canEdit ? 'Add a Mission Thread before authoring Tasks.' : undefined)
        }
        onClick={editableEmptyThreadAction?.onClick}
        title="No Mission Threads"
      />
    );
  }

  return (
    <div className="flex min-h-[calc(100svh-var(--impersonation-banner-height,0px)-10rem)] flex-1 flex-col gap-4">
      <MeiaThreadTabsTile
        canEdit={canEdit}
        missionId={missionId}
        onSelectThread={handleSelectThread}
        selectedThreadId={validThreadId}
        studyId={studyId}
      />
      {validThread && (
        <MeiaTaskCanvas
          canEdit={canEdit}
          missionId={missionId}
          studyId={studyId}
          thread={validThread}
        />
      )}
    </div>
  );
};
